import type { FormEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowRight,
  ChevronsUp,
  Eye,
  EyeOff,
  IdCard,
  Loader2,
  LockKeyhole,
  Phone,
} from "lucide-react";
import { errorMessage, request, revokeSession } from "../lib/api";
import { ConsentModal } from "../components/ConsentModal";
import { LanguageSwitcher } from "../components/ui";
import { canCallEmergency, emergencyDialNumber, emergencyPhoneNumber } from "../lib/helpline";
import type { LoginResponse, Session } from "../types";

const ALLOWED_ROLES = ["teacher", "facilitator"];

export function Login({ onLogin }: { onLogin: (session: Session) => void }) {
  const { t } = useTranslation();
  const [assignedID, setAssignedID] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // A sign-in that still needs consent. Held here, not saved: the session
  // only reaches storage once consent is recorded, so declining leaves
  // nothing behind on the device.
  const [pending, setPending] = useState<Session | null>(null);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentError, setConsentError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await request<LoginResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({
          assigned_id: assignedID,
          password,
          phone_time: new Date().toISOString(),
          connection: navigator.onLine ? "online" : "offline",
          device: { user_agent: navigator.userAgent },
        }),
      });
      if (!ALLOWED_ROLES.includes(result.user.role)) {
        void revokeSession(result.token).catch(() => undefined);
        throw new Error(t("useAdminPortal"));
      }
      const session: Session = {
        token: result.token,
        assignedID: result.user.assigned_id,
        role: result.user.role,
      };
      if (result.user.consent_accepted) onLogin(session);
      else setPending(session);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function acceptConsent() {
    if (!pending) return;
    setConsentBusy(true);
    setConsentError("");
    try {
      // The token isn't in storage yet, so it is passed explicitly.
      await request("/api/me/consent", {
        method: "POST",
        headers: { Authorization: `Bearer ${pending.token}` },
      });
      onLogin(pending);
    } catch (cause) {
      setConsentError(errorMessage(cause));
      setConsentBusy(false);
    }
  }

  function declineConsent() {
    if (pending) {
      void revokeSession(pending.token).catch(() => undefined);
    }
    setPending(null);
    setConsentError("");
    setPassword("");
  }

  return (
    <div className="flex min-h-screen w-full justify-center bg-surface">
      <main className="flex min-h-screen w-full max-w-115.75 flex-col bg-app-canvas px-8 pb-10 pt-16">
        <section className="text-center">
          <div className="mx-auto flex w-fit items-center gap-2 text-start">
            <span className="grid size-8 place-items-center rounded-[9px] bg-accent-brand text-white shadow-sm">
              <ChevronsUp size={20} strokeWidth={3} />
            </span>
            <span className="leading-none">
              <strong className="block text-base font-bold tracking-[-0.035em] text-app-ink">
                CPD <span className="text-accent">Training</span>
              </strong>
            </span>
          </div>

          <div className="mt-8">
            <LanguageSwitcher />
          </div>

          <h1 className="mt-14 text-[32px] leading-9 font-bold tracking-[-0.045em] text-app-ink">
            {t("signInTitle")}
          </h1>
          <p className="mx-auto mt-4 max-w-95 text-base leading-[1.65] text-app-copy">
            {t("signInHelp")}
          </p>
        </section>

        <form
          className="mt-12 grid gap-6 rounded-[14px] bg-white px-7 py-8 shadow-card"
          onSubmit={(event) => void submit(event)}
        >
          <label className="grid gap-2 text-sm font-semibold text-app-ink">
            {t("cerpIdOrUsername")}
            <span className="relative block">
              <IdCard
                className="absolute inset-s-4 top-1/2 -translate-y-1/2 text-app-icon"
                size={19}
              />
              <input
                className="h-13 w-full rounded-[10px] border-0 bg-app-field ps-12 pe-4 text-base font-normal text-app-ink placeholder:text-app-icon focus:outline-none focus:ring-2 focus:ring-accent/25"
                value={assignedID}
                onChange={(event) => setAssignedID(event.target.value)}
                placeholder={t("facilitatorIdExample")}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
              />
            </span>
          </label>

          <label className="grid gap-2 text-sm font-semibold text-app-ink">
            {t("password")}
            <span className="relative block">
              <LockKeyhole
                className="absolute inset-s-4 top-1/2 -translate-y-1/2 text-app-icon"
                size={19}
              />
              <input
                className="h-13 w-full rounded-[10px] border-0 bg-app-field ps-12 pe-12 text-base font-normal text-app-ink placeholder:text-app-icon focus:outline-none focus:ring-2 focus:ring-accent/25"
                type={passwordVisible ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••••••"
                autoComplete="current-password"
                required
              />
              <button
                className="absolute inset-y-0 inset-e-0 grid w-12 place-items-center text-app-icon transition-colors hover:text-accent"
                type="button"
                onClick={() => setPasswordVisible((visible) => !visible)}
                aria-label={passwordVisible ? t("hidePassword") : t("showPassword")}
                aria-pressed={passwordVisible}
              >
                {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </span>
          </label>

          {error && (
            <p className="flex items-center gap-1.5 rounded-md bg-danger-bg px-3 py-2 text-sm text-accent-strong">
              <AlertTriangle size={16} className="shrink-0" />
              {error}
            </p>
          )}

          <button
            className="mt-2 flex h-14.5 w-full items-center justify-center gap-3 rounded-[10px] bg-accent text-base font-semibold text-white shadow-sm transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
            disabled={busy}
          >
            {busy && <Loader2 size={17} className="animate-spin" />}
            {busy ? t("signingIn") : t("login")}
            {!busy && <ArrowRight size={20} />}
          </button>
        </form>

        {canCallEmergency && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center">
            <p className="text-sm text-app-copy">{t("loginHelplinePrompt")}</p>
            <a
              className="inline-flex min-h-10 items-center gap-2 rounded-full bg-accent-strong px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-accent-strong-hover active:scale-[0.98]"
              href={`tel:${emergencyDialNumber}`}
              dir="ltr"
            >
              <Phone size={16} />
              {emergencyPhoneNumber}
            </a>
          </div>
        )}
      </main>

      <ConsentModal
        key={pending?.token ?? "none"}
        open={pending !== null}
        busy={consentBusy}
        error={consentError}
        onAccept={() => void acceptConsent()}
        onDecline={declineConsent}
      />
    </div>
  );
}
